import { Component, OnInit, Inject, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WhatsappService } from '../../shared/services/whatsapp.service';
import { EvolutionInstanceService } from '../../shared/services/evolution-instance.service';
import { AppConfirmService } from '../../shared/services/app-confirm/app-confirm.service';
import { HttpEventType } from '@angular/common/http';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { warn } from 'console';
import { WhatsAppConfigService } from '../utilitario/whatsapp-config/whatsapp-config.service';
import { JwtAuthService } from '../../shared/services/auth/jwt-auth.service';
import { AppLoaderService } from '../../shared/services/app-loader/app-loader.service';
import { TxtAddService } from '../../shared/services/txtAdd.service';
import { TxtAdd } from '../../shared/interfaces/txtAdd';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-whatsapp-edit',
  templateUrl: './whatsapp-edit.component.html',
  styleUrls: ['./whatsapp-edit.component.css']
})
export class WhatsappEditComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  attachments: File[] = [];
  sending = false;
  instanceName: string | null = null;

  // Histórico e retry
  sendHistory: Array<{
    timestamp: Date;
    status: 'success' | 'error' | 'pending';
    recipientCount: number;
    successCount?: number;
    errorCount?: number;
    errors?: any[];
  }> = [];
  lastErrors: any[] = [];
  showRetryOption = false;
  retrying = false;

  // Lookup Texto Adicional
  showTxtAddLookup = false;
  txtAddResults: TxtAdd[] = [];
  txtAddSearch = '';
  txtAddLoading = false;
  private txtAddSearch$ = new Subject<string>();
  private static readonly TXTADD_SEARCH_KEY = 'whatsapp_txtadd_last_search';

  constructor(
    private fb: FormBuilder,
    private whatsappConfigService: WhatsAppConfigService,
    private confirmService: AppConfirmService,
    private jwtAuth: JwtAuthService,
    private whatsappService: WhatsappService,
    private loader: AppLoaderService,
    private txtAddService: TxtAddService,
    public dialogRef: MatDialogRef<WhatsappEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    const toValidators = (data && data.to) ? [] : [Validators.required];
    this.form = this.fb.group({
      to: ['', toValidators],
      message: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.txtAddSearch$.pipe(
      debounceTime(400),
      switchMap(term => {
        this.txtAddLoading = true;
        return term
          ? this.txtAddService.getTxtAddPageSearch(1, 20, term)
          : this.txtAddService.getTxtAddPage(1, 20);
      })
    ).subscribe(page => {
      this.txtAddResults = page.content || [];
      this.txtAddLoading = false;
    }, () => this.txtAddLoading = false);
  }

  ngAfterViewInit(): void {
    // preencher valores iniciais passados via MAT_DIALOG_DATA
    try {
      if (this.data) {
        if (this.data.to) {
          this.form.controls['to'].setValue(this.data.to);
        }
        if (this.data.message) {
          this.form.controls['message'].setValue(this.data.message);
        }
        // aceitar arquivos passados como Blobs ou Files
        if (this.data.files && Array.isArray(this.data.files) && this.data.files.length > 0) {
          this.data.files.forEach((f: any, idx: number) => {
            if (f instanceof File) {
              this.attachments.push(f);
            } else if (f instanceof Blob) {
              const name = (f as any).name || (`attachment-${idx + 1}.pdf`);
              this.attachments.push(new File([f], name, { type: (f as any).type || 'application/pdf' }));
            } else if (f && f.blob && f.name) {
              this.attachments.push(new File([f.blob], f.name, { type: f.blob.type || 'application/pdf' }));
            }
          });
        }
      }
    } catch (e) {
      console.warn('Erro ao popular dados iniciais do WhatsApp dialog', e);
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files.item(i);
      if (file) {
        this.attachments.push(file);
      }
    }
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
  }

  /**
   * Extrair informações úteis do objeto de erro
   */
  private extractErrorInfo(err: any): any {
    if (err.type && err.userMessage) {
      return {
        type: err.type,
        message: err.message,
        userMessage: err.userMessage,
        status: err.status
      };
    }

    if (err instanceof Error) {
      return {
        type: 'GENERIC_ERROR',
        message: err.message,
        userMessage: err.message || 'Erro desconhecido',
        status: null
      };
    }

    if (typeof err === 'object') {
      return {
        type: err.type || err.error?.type || 'UNKNOWN',
        message: err.message || err.error?.message || 'Erro desconhecido',
        userMessage: err.userMessage || err.error?.userMessage || 'Erro desconhecido',
        status: err.status || err.error?.status || null
      };
    }

    return {
      type: 'UNKNOWN',
      message: String(err),
      userMessage: 'Erro desconhecido',
      status: null
    };
  }

  /**
   * Formatar resumo de erros de forma amigável ao usuário
   */
  private formatErrorSummary(errors: any[], successes: any[], total: number): string {
    const errorsByType: { [key: string]: any[] } = {};

    // Agrupar erros por tipo
    errors.forEach(err => {
      const errorObj = err.error?.originalError || err.error || err;
      const errorType = errorObj.type || 'UNKNOWN';
      const userMessage = errorObj.userMessage || errorObj.message || 'Erro desconhecido';

      if (!errorsByType[errorType]) {
        errorsByType[errorType] = [];
      }
      errorsByType[errorType].push({
        number: err.number,
        message: userMessage,
        step: err.step
      });
    });

    // Construir mensagem formatada
    let message = `Enviadas: ${successes.length} / ${total}. Erros: ${errors.length}.\n\n`;
    message += 'Detalhes dos erros:\n\n';

    Object.keys(errorsByType).forEach(type => {
      const errsOfType = errorsByType[type];
      const typeLabel = this.getErrorTypeLabel(type);
      message += `${typeLabel} (${errsOfType.length}):\n`;

      errsOfType.slice(0, 5).forEach((err) => {
        const userMsg = err.message?.length > 50
          ? err.message.substring(0, 47) + '...'
          : err.message;
        message += `  • ${err.number}: ${userMsg}\n`;
      });

      if (errsOfType.length > 5) {
        message += `  ... e mais ${errsOfType.length - 5} erro(s)\n`;
      }

      message += '\n';
    });

    message += 'Verifique os detalhes completos no console do navegador (F12).';
    return message;
  }

  /**
   * Obter label amigável para tipo de erro
   */
  private getErrorTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'NETWORK_ERROR': 'Erro de Conectividade',
      'CONNECTION_ERROR': 'Falha na Conexão',
      'VALIDATION_ERROR': 'Formato Inválido',
      'AUTH_ERROR': 'Falha de Autenticação',
      'FORBIDDEN_ERROR': 'Acesso Proibido',
      'NOT_FOUND_ERROR': 'Instância Não Encontrada',
      'TIMEOUT_ERROR': 'Tempo Limite Excedido',
      'RATE_LIMIT_ERROR': 'Limite de Requisições',
      'SERVER_ERROR': 'Erro do Servidor',
      'SERVICE_UNAVAILABLE_ERROR': 'Serviço Indisponível',
      'API_ERROR': 'Erro da API',
      'UNKNOWN': 'Erro Desconhecido'
    };
    return labels[type] || labels['UNKNOWN'];
  }

  /**
   * Registrar tentativa de envio no histórico
   */
  private registrarTentativa(
    recipientCount: number,
    successCount: number,
    errorCount: number,
    errors: any[]
  ): void {
    this.sendHistory.push({
      timestamp: new Date(),
      status: errorCount === 0 ? 'success' : (successCount === 0 ? 'error' : 'pending'),
      recipientCount,
      successCount,
      errorCount,
      errors: errorCount > 0 ? errors : undefined
    });

    // Manter apenas últimas 20 tentativas
    if (this.sendHistory.length > 20) {
      this.sendHistory.shift();
    }
  }

  /**
   * Copiar erros para clipboard
   */
  copiarErrosParaClipboard(): void {
    if (this.lastErrors.length === 0) {
      this.confirmService.confirm({
        title: 'Nenhum erro',
        message: 'Não há erros para copiar.',
        icon: 'info',
        disableCancel: true
      });
      return;
    }

    let texto = `Erros de Envio WhatsApp - ${new Date().toLocaleString('pt-BR')}\n`;
    texto += '='.repeat(60) + '\n\n';

    this.lastErrors.forEach((err: any, idx: number): void => {
      texto += `${idx + 1}. Número: ${err.number}\n`;
      texto += `   Tipo: ${err.error?.type || 'UNKNOWN'}\n`;
      texto += `   Mensagem: ${err.error?.message || 'Erro desconhecido'}\n`;
      texto += `   Etapa: ${err.step || 'desconhecida'}\n\n`;
    });

    navigator.clipboard.writeText(texto).then((): void => {
      this.confirmService.confirm({
        title: 'Copiado!',
        message: 'Detalhes dos erros foram copiados para a área de transferência.',
        icon: 'check_circle',
        disableCancel: true
      });
    }).catch((err: any): void => {
      console.error('Erro ao copiar para clipboard:', err);
      this.confirmService.confirm({
        title: 'Erro ao copiar',
        message: 'Falha ao copiar para clipboard. Verifique as permissões do navegador.',
        error: true,
        icon: 'error',
        disableCancel: true
      });
    });
  }

  /**
   * Reenviar apenas as mensagens que falharam
   */
  reenviarFalhas(): void {
    if (this.lastErrors.length === 0) {
      this.confirmService.confirm({
        title: 'Nenhum erro para reenviar',
        message: 'Todas as mensagens foram enviadas com sucesso.',
        icon: 'check_circle',
        disableCancel: true
      });
      return;
    }

    const nums = this.lastErrors.map(e => e.number).join(', ');
    this.confirmService.confirm({
      title: 'Confirmar reenvio',
      message: `Deseja reenviar ${this.lastErrors.length} mensagem(ns) que falharam?\n\nNúmeros: ${nums}`,
      okText: 'Reenviar',
      cancelText: 'Cancelar'
    }).subscribe((confirmed: any) => {
      if (!confirmed) {
        return;
      }

      this.retrying = true;
      this.loader.open('Reenviando mensagens...');

      const currentUser = this.jwtAuth.getUser();
      const username = currentUser?.usuario;
      const message = this.form.value.message;

      let done = 0;
      const retryErrors = [...this.lastErrors];
      const newErrors: any[] = [];
      const newSuccesses: any[] = [];

      const finalizeRetry = (): void => {
        this.loader.close();
        this.retrying = false;

        if (newErrors.length === 0) {
          this.lastErrors = [];
          this.showRetryOption = false;
          this.confirmService.confirm({
            title: 'Reenvio bem-sucedido',
            message: `${newSuccesses.length} mensagem(ns) reenviada(s) com sucesso.`,
            icon: 'check_circle',
            disableCancel: true
          });
        } else {
          this.lastErrors = newErrors;
          const errorSummary = this.formatErrorSummary(newErrors, newSuccesses, retryErrors.length);
          this.confirmService.confirm({
            title: 'Reenvio concluído com erros',
            message: errorSummary,
            error: true,
            icon: 'warning',
            disableCancel: true
          });
        }
      };

      retryErrors.forEach((errItem: any) => {
        const numero = errItem.number;

        if (this.attachments.length > 0) {
          const baseMediaType = this.attachments[0].type || '';
          const mediatype = baseMediaType.includes('image') ? 'image' :
            baseMediaType.includes('video') ? 'video' : 'document';

          Promise.all(this.attachments.map(file => {
            return new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (): void => {
                const result = reader.result as string;
                const base64 = result.split(',')[1] || result;
                resolve(base64);
              };
              reader.readAsDataURL(file);
            });
          })).then((filesBase64: string[]) => {
            const payload: any = {
              number: numero,
              caption: message,
              files: filesBase64,
              filenames: this.attachments.map(f => f.name),
              mediatype: mediatype
            };

            this.enviarComRetry(username, payload, numero, newSuccesses, newErrors,
              retryErrors.length, (): void => {
                done++;
                if (done === retryErrors.length) {
                  finalizeRetry();
                }
              });
          });
        } else {
          this.enviarComRetry(username, null, numero, newSuccesses, newErrors,
            retryErrors.length, (): void => {
              done++;
              if (done === retryErrors.length) {
                finalizeRetry();
              }
            }, message);
        }
      });
    });
  }

  /**
   * Enviar com retry automático para erros de rede
   */
  private enviarComRetry(
    username: string,
    payload: any | null,
    numero: string,
    successes: any[],
    errors: any[],
    total: number,
    callback: () => void,
    message?: string
  ): void {
    const maxRetries = 3;
    const baseDelay = 1000;

    const tentarEnvio = (tentativa: number = 0): void => {
      if (payload) {
        this.whatsappService.sendMedia(username, payload).subscribe({
          next: (): void => {
            successes.push({ number: numero });
            callback();
          },
          error: (err: any): void => {
            const errorInfo = this.extractErrorInfo(err);
            const isNetworkError = errorInfo.type === 'NETWORK_ERROR' ||
              errorInfo.type === 'CONNECTION_ERROR' ||
              errorInfo.type === 'TIMEOUT_ERROR';

            if (isNetworkError && tentativa < maxRetries) {
              const delay = baseDelay * Math.pow(2, tentativa);
              console.log(`Tentando novamente em ${delay}ms (tentativa ${tentativa + 1}/${maxRetries})...`);
              setTimeout((): void => {
                tentarEnvio(tentativa + 1);
              }, delay);
            } else {
              errors.push({ number: numero, step: 'sendMedia', error: errorInfo });
              callback();
            }
          }
        });
      } else if (message) {
        this.whatsappService.sendText(username, numero, message).subscribe({
          next: (): void => {
            successes.push({ number: numero });
            callback();
          },
          error: (err: any): void => {
            const errorInfo = this.extractErrorInfo(err);
            const isNetworkError = errorInfo.type === 'NETWORK_ERROR' ||
              errorInfo.type === 'CONNECTION_ERROR' ||
              errorInfo.type === 'TIMEOUT_ERROR';

            if (isNetworkError && tentativa < maxRetries) {
              const delay = baseDelay * Math.pow(2, tentativa);
              console.log(`Tentando novamente em ${delay}ms (tentativa ${tentativa + 1}/${maxRetries})...`);
              setTimeout((): void => {
                tentarEnvio(tentativa + 1);
              }, delay);
            } else {
              errors.push({ number: numero, step: 'sendText', error: errorInfo });
              callback();
            }
          }
        });
      }
    };

    tentarEnvio();
  }

  send(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const toRaw = this.form.value.to;
    const toList: string[] = [];
    const invalidNumbers: string[] = [];

    if (toRaw && typeof toRaw === 'string') {
      const nums = toRaw.split(';').map(num => {
        let n = num.replace(/\D/g, '');
        if (n && !n.startsWith('55')) {
          n = '55' + n;
        }
        if (!/^55\d{2}(9\d{8}|[2-5]\d{7})$/.test(n)) {
          invalidNumbers.push(num.trim());
        }
        return n;
      }).filter(n => n.length > 0);
      toList.push(...nums);
    }

    if (invalidNumbers.length > 0) {
      this.confirmService.confirm({
        title: 'Números inválidos',
        message: `Os seguintes números estão com formato inválido: ${invalidNumbers.join(', ')}`,
        error: true,
        icon: 'warning',
        disableCancel: true
      });
      return;
    }

    const message = this.form.value.message;
    this.sending = true;

    const currentUser = this.jwtAuth.getUser();
    this.instanceName = currentUser?.username || null;
    const username = currentUser?.usuario;

    this.whatsappConfigService.fetchInstance(username).subscribe({
      next: (instance: any): void => {
        let found = false;
        const matchesUsername = (i: any, usernameToCheck: string): boolean => {
          return (
            i !== null &&
            (i.name === usernameToCheck ||
              i.instanceName === usernameToCheck ||
              i.instance?.instanceName === usernameToCheck ||
              i.instance?.name === usernameToCheck)
          );
        };

        if (Array.isArray(instance)) {
          found = instance.some(i => matchesUsername(i, username));
        } else if (instance) {
          found = matchesUsername(instance, username);
        }

        let isConnected = false;
        if (found) {
          const findInst = Array.isArray(instance) ?
            instance.find(i => matchesUsername(i, username)) : instance;
          const instStatus = (findInst?.connectionStatus ||
            findInst?.instance?.connectionStatus ||
            findInst?.instance?.status ||
            findInst?.status)?.toString().toLowerCase();
          if (instStatus === 'open' || instStatus === 'connected') {
            isConnected = true;
          }
        }

        if (!found || !isConnected) {
          this.loader.close();
          this.sending = false;
          this.confirmService.confirm({
            title: 'Instância WhatsApp não conectada',
            message: 'Configure uma instância em Utilitários > Configuração WhatsApp.',
            error: true,
            icon: 'warning',
            disableCancel: true
          });
          return;
        }

        this.loader.open('Enviando...');
        const total = toList.length;

        if (total === 0) {
          this.loader.close();
          this.sending = false;
          this.confirmService.confirm({
            title: 'Nenhum número informado',
            message: 'Informe ao menos um número para envio.',
            error: true,
            icon: 'warning',
            disableCancel: true
          });
          return;
        }

        let done = 0;
        const successes: any[] = [];
        const errors: any[] = [];

        const finalize = (): void => {
          this.loader.close();
          this.sending = false;
          this.registrarTentativa(total, successes.length, errors.length, errors);

          if (errors.length === 0) {
            this.form.reset();
            this.attachments = [];
            this.lastErrors = [];
            this.showRetryOption = false;
            this.confirmService.confirm({
              title: 'Mensagens enviadas',
              message: 'Todas as mensagens foram enviadas com sucesso.',
              icon: 'check_circle',
              disableCancel: true
            });
            this.dialogRef.close({ success: true });
          } else {
            this.lastErrors = errors;
            this.showRetryOption = true;
            const errorSummary = this.formatErrorSummary(errors, successes, total);
            this.confirmService.confirm({
              title: 'Envio concluído com erros',
              message: errorSummary,
              error: true,
              icon: 'warning',
              disableCancel: true
            });
            console.error('Detalhes dos erros de envio:', errors);
          }
        };

        const detectMediaType = (): string | undefined => {
          if (this.attachments.length === 0) {
            return undefined;
          }
          const mime = this.attachments[0].type || '';
          if (mime.startsWith('image/')) {
            return 'image';
          }
          if (mime.startsWith('video/')) {
            return 'video';
          }
          if (mime.startsWith('audio/')) {
            return 'audio';
          }
          return 'document';
        };

        const filesToBase64Once = (): Promise<string[]> => {
          if (this.attachments.length === 0) {
            return Promise.resolve([]);
          }
          return Promise.all(this.attachments.map(file => {
            return new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (): void => {
                const result = reader.result as string;
                const base64 = result.split(',')[1] || result;
                resolve(base64);
              };
              reader.onerror = (): void => {
                reject(reader.error);
              };
              reader.readAsDataURL(file);
            });
          }));
        };

        filesToBase64Once().then((filesBase64: string[]): void => {
          const filenames = this.attachments.map(f => f.name);
          const mediatype = detectMediaType();

          toList.forEach((number: string): void => {
            if (this.attachments.length > 0) {
              const payload: any = {
                number,
                caption: message,
                files: filesBase64,
                filenames,
                mediatype
              };
              this.whatsappService.sendMedia(username, payload).subscribe({
                next: (): void => {
                  successes.push({ number });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                },
                error: (err: any): void => {
                  errors.push({
                    number,
                    step: 'sendMedia',
                    error: this.extractErrorInfo(err)
                  });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                }
              });
            } else {
              this.whatsappService.sendText(username, number, message).subscribe({
                next: (): void => {
                  successes.push({ number });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                },
                error: (err: any): void => {
                  errors.push({
                    number,
                    step: 'sendText',
                    error: this.extractErrorInfo(err)
                  });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                }
              });
            }
          });
        }).catch((err: any): void => {
          this.loader.close();
          this.sending = false;
          this.confirmService.confirm({
            title: 'Erro na leitura dos arquivos',
            message: 'Falha ao converter arquivos para base64.',
            error: true,
            icon: 'error',
            disableCancel: true
          });
          console.error('Erro ao converter arquivos:', err);
        });
      },
      error: (): void => {
        this.loader.close();
        this.sending = false;
        this.confirmService.confirm({
          title: 'Falha na verificação',
          message: 'Falha ao verificar instância WhatsApp Evolution.',
          error: true,
          disableCancel: true
        });
      }
    });
  }

  // ── Lookup Texto Adicional ──

  toggleTxtAddLookup(): void {
    this.showTxtAddLookup = !this.showTxtAddLookup;
    if (this.showTxtAddLookup) {
      const lastSearch = localStorage.getItem(WhatsappEditComponent.TXTADD_SEARCH_KEY) || '';
      if (lastSearch && !this.txtAddSearch) {
        this.txtAddSearch = lastSearch;
        this.txtAddSearch$.next(lastSearch);
      } else if (this.txtAddResults.length === 0) {
        this.loadTxtAddPage();
      }
    }
  }

  onTxtAddSearchChange(term: string): void {
    this.txtAddSearch = term;
    this.txtAddSearch$.next(term);
  }

  loadTxtAddPage(): void {
    this.txtAddLoading = true;
    this.txtAddService.getTxtAddPage(1, 20).subscribe(
      (page: any): void => {
        this.txtAddResults = page.content || [];
        this.txtAddLoading = false;
      },
      (): void => {
        this.txtAddLoading = false;
      }
    );
  }

  selectTxtAdd(item: TxtAdd): void {
    const current = this.form.controls['message'].value || '';
    const texto = item.texto || '';
    this.form.controls['message'].setValue(current ? current + '\n' + texto : texto);
    localStorage.setItem(WhatsappEditComponent.TXTADD_SEARCH_KEY, this.txtAddSearch);
    this.showTxtAddLookup = false;
  }

  cancel(): void {
    this.dialogRef.close({ success: false, cancelled: true });
  }
}
